import React, { useEffect } from 'react';
import { CircularProgress, Paper } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getEvent } from '../../actions/events';
import { EventLearningCard } from '../Events/EventLearningCard';
import { supportsEventEnrichment } from '../../lib/eventEnrichment';

function prettyDate(value) {
  if (!value) return 'Timing TBD';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_error) {
    return value;
  }
}

export const EventDetails = () => {
  const { event, isLoading } = useSelector((state) => state.events);
  const dispatch = useDispatch();
  const params = useParams();
  const id = decodeURIComponent(params.id || '');

  useEffect(() => {
    dispatch(getEvent(id));
  }, [id, dispatch]);

  if (isLoading) {
    return (
      <div className="atlas-route-shell atlas-detail-page">
        <Paper elevation={6} className="atlas-detail-card atlas-loading-shell">
          <CircularProgress size="3rem" />
        </Paper>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="atlas-route-shell atlas-detail-page">
        <div className="atlas-empty-state">This event record is unavailable right now.</div>
      </div>
    );
  }

  const title = event.name || event.title || event.item_type_id || id;
  const category = event.category_label || event.category || 'Event';
  const locations = event.locations || event.location_name || event.event_type || 'Location pending';
  const description = event.description || event.summary || 'No additional description yet.';
  const reason = event.reason || event.source || 'No reason supplied.';
  const status = event.status || 'scheduled';
  const startsAt = prettyDate(event.starts_at || event.start_ts);
  const hasOpenExplainer = supportsEventEnrichment(event);

  return (
    <div className="atlas-route-shell atlas-detail-page">
      <section className="atlas-page-header">
        <span className="atlas-eyebrow">Event brief</span>
        <h1>{title}</h1>
        <p>
          Use this page to understand what the event is, why it matters, and where to go for an
          open-source explainer if you want the science context behind the signal.
        </p>
        <div className="atlas-pill-row">
          <span className="atlas-pill">{category}</span>
          <span className="atlas-pill">{status}</span>
          <span className="atlas-pill">{startsAt}</span>
        </div>
      </section>

      <div className="atlas-detail-grid">
        <section className="atlas-detail-stack">
          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">At a glance</span>
            <div className="atlas-detail-list">
              <span>
                <strong>Locations</strong>
                {locations}
              </span>
              <span>
                <strong>Description</strong>
                {description}
              </span>
              <span>
                <strong>Why it matters</strong>
                {reason}
              </span>
            </div>
          </Paper>
        </section>

        <section className="atlas-detail-stack">
          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Open-source explainer</span>
            {hasOpenExplainer ? (
              <EventLearningCard item={event} />
            ) : (
              <div className="atlas-detail-list">
                <span>
                  <strong>Explainer availability</strong>
                  Open-source event explainers are currently focused on celestial and Earth-system signals.
                </span>
              </div>
            )}
          </Paper>
        </section>
      </div>
    </div>
  );
};
